import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';

import { Accordion, AccordionDetails, AccordionSummary, Box, Stack } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

import { InfoField } from '../../../components';
import { useGetProfileSumsubDataQuery } from '../../../api';
import { UTCToDateTime } from '../../../utils/dates';

export const SumsubInfo: FC = () => {
  const { t } = useTranslation();
  const { profileUid } = useParams<'profileUid'>();

  // api
  const { data: sumsubData } = useGetProfileSumsubDataQuery(
    profileUid ? { profileUid } : skipToken
  );

  // parsing data
  const getFilteredData = (data: Record<string, any> | undefined, excludeKeys: string[]) => {
    if (!data) return [];

    return Object.keys(data)
      .filter((key) => !excludeKeys.includes(key))
      .reduce(
        (obj, key) => {
          obj[key] = data[key];
          return obj;
        },
        {} as Record<string, any>
      );
  };

  const reviewResultData = getFilteredData(sumsubData?.review.reviewResult, []);
  const infoData = getFilteredData(sumsubData?.info, ['idDocs', 'addresses']);
  const fixedInfoData = getFilteredData(sumsubData?.fixedInfo, ['addresses']);
  const notesData = getFilteredData(sumsubData?.notes, []);

  // recursive function for values
  const renderInfoFields = (data: object) => {
    return Object.entries(data).map(([key, value], index, array) => {
      const isLast = array.length === index + 1;

      if (typeof value === 'object' && value !== null) {
        return (
          <Accordion key={key} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>{key}</AccordionSummary>
            <AccordionDetails>
              <Stack direction="column" spacing={1}>
                {renderInfoFields(value)}
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      }

      return <InfoField key={key} name={key} value={value} withoutDivider={isLast} />;
    });
  };

  return (
    <Box>
      {/*Review status*/}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {t('profile-sumsub-info.panels.review-status')}
        </AccordionSummary>

        <AccordionDetails>
          <Stack direction="column" spacing={1}>
            <InfoField
              name={t('profile-sumsub-info.review-status.level-name')}
              value={sumsubData?.review.levelName}
            />

            <InfoField
              name={t('profile-sumsub-info.review-status.review-date')}
              value={sumsubData?.review.reviewDate}
              formattedValue={
                sumsubData?.review.reviewDate && UTCToDateTime(sumsubData.review.reviewDate)
              }
            />

            <InfoField
              name={t('profile-sumsub-info.review-status.review-status')}
              value={sumsubData?.review.reviewStatus}
            />

            {sumsubData?.review.reviewResult && (
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  {t('profile-sumsub-info.review-status.review-result')}
                </AccordionSummary>

                <AccordionDetails>
                  <Stack direction="column" spacing={1}>
                    {renderInfoFields(reviewResultData)}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/*Info*/}
      {sumsubData?.info && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            {t('profile-sumsub-info.panels.info')}
          </AccordionSummary>

          <AccordionDetails>
            <Stack direction="column" spacing={1}>
              {renderInfoFields(infoData)}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {/*Addresses №*/}
      {sumsubData?.info?.addresses &&
        sumsubData.info.addresses.map((address, index) => {
          const addressData = getFilteredData(address, []);

          return (
            <Accordion defaultExpanded key={index}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                {t('profile-sumsub-info.panels.address', { value: index + 1 })}
              </AccordionSummary>

              <AccordionDetails>
                <Stack direction="column" spacing={1}>
                  {renderInfoFields(addressData)}
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}

      {/*Documents*/}
      {sumsubData?.info?.idDocs &&
        sumsubData.info.idDocs.map((doc, index) => {
          const docData = getFilteredData(doc, []);

          return (
            <Accordion defaultExpanded key={index}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                {`${index + 1}. ${doc.idDocType}`}
              </AccordionSummary>

              <AccordionDetails>
                <Stack direction="column" spacing={1}>
                  {renderInfoFields(docData)}
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}

      {/*Fixed info*/}
      {sumsubData?.fixedInfo && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            {t('profile-sumsub-info.panels.fixed-info')}
          </AccordionSummary>

          <AccordionDetails>
            <Stack direction="column" spacing={1}>
              {renderInfoFields(fixedInfoData)}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {/*Fixed addresses №*/}
      {sumsubData?.fixedInfo?.addresses &&
        sumsubData.fixedInfo.addresses.map((address, index) => {
          const fixedAddressData = getFilteredData(address, []);

          return (
            <Accordion defaultExpanded key={index}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                {t('profile-sumsub-info.panels.fixed-address', { value: index + 1 })}
              </AccordionSummary>

              <AccordionDetails>
                <Stack direction="column" spacing={1}>
                  {renderInfoFields(fixedAddressData)}
                </Stack>
              </AccordionDetails>
            </Accordion>
          );
        })}

      {/*Notes*/}
      {sumsubData?.notes && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            {t('profile-sumsub-info.panels.notes')}
          </AccordionSummary>

          <AccordionDetails>
            <Stack direction="column" spacing={1}>
              {renderInfoFields(notesData)}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};
